import { useState, useRef, useCallback, useEffect } from 'react';
import { Image, Smile, Code, X, Info, Check, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/useToast';
import { apiClient } from '@/services/apiClient';
import { SMS_VARIABLES, SMS_MAX_CHARS, MMS_MAX_FILES, MMS_MAX_BYTES, SMS_OPT_OUT_TOKEN, isMmsSendableUrl, mmsMediaRejectionReason, segmentColorClass, hasSmsOptOut, hasSmsMessageContent, ensureSmsOptOut, smsBodyWithoutOptOut } from './sms-editor-constants';
import SmsEmojiPicker from './SmsEmojiPicker';
import SmsPhotoPicker from './SmsPhotoPicker';

export interface SmsEditorProps {
  messageBody: string;
  onMessageChange: (val: string) => void;
  mediaUrls: string[];
  onMediaChange: (urls: string[]) => void;
  /** Extra className for the photo picker Modal wrapper (e.g. to raise z-index) */
  photoPickerZClassName?: string;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 KB';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1_048_576) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1_048_576).toFixed(2) + ' MB';
}

function sizeColorClass(size: number): string {
  if (size > MMS_MAX_BYTES) return 'red';
  if (size > 3_145_728) return 'orange';
  if (size > 1_048_576) return 'blue';
  return 'green';
}

const FILL_BG: Record<string, string> = {
  green: 'bg-green-500',
  blue: 'bg-sky-500',
  orange: 'bg-orange-500',
  red: 'bg-red-500',
};
const SIZE_TEXT: Record<string, string> = {
  green: 'text-green-600',
  blue: 'text-sky-600',
  orange: 'text-orange-500',
  red: 'text-red-500',
};

export default function SmsEditor({
  messageBody,
  onMessageChange,
  mediaUrls,
  onMediaChange,
  photoPickerZClassName,
}: SmsEditorProps) {
  const toast = useToast();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const varPanelRef = useRef<HTMLDivElement>(null);

  const [emojiOpen, setEmojiOpen] = useState(false);
  const [varOpen, setVarOpen] = useState(false);
  const [photoPickerOpen, setPhotoPickerOpen] = useState(false);
  const [varDefaults, setVarDefaults] = useState<Record<string, string>>({});
  // What {{opt_out}} says for this account: Marketing Settings → SMS, or the
  // default when unset. The API resolves the default, so this is never blank
  // once loaded.
  const [optOutText, setOptOutText] = useState('');

  useEffect(() => {
    apiClient.get('/marketing/settings').then((res) => {
      const vd = res.data?.data?.variableDefaults;
      if (vd && typeof vd === 'object') setVarDefaults(vd);
      const text = res.data?.data?.optOutText;
      if (typeof text === 'string' && text.trim()) setOptOutText(text.trim());
    }).catch(() => {});
  }, []);

  // The account text only applies through the token; written-out instructions
  // ("Text STOP to end") are sent as typed.
  const usesOptOutToken = smsBodyWithoutOptOut(messageBody) !== messageBody;

  // Mutable size cache -- exactly like PHP's mediaSizesData object.
  // We use a ref so fetches don't fight with React's render cycle.
  const sizesRef = useRef<Record<string, number | null>>({});
  // A counter we bump to force a re-render after a size resolves.
  const [, setSizeVersion] = useState(0);

  const charCount = messageBody.length;
  const hasUnicode = /[^\x00-\x7F]/.test(messageBody);
  const segLen = hasUnicode ? 70 : 160;
  const segments = charCount === 0 ? 0 : Math.ceil(charCount / segLen);

  // Exactly replicates PHP fetchMediaSize: marks URL as loading, hits
  // the server-side proxy, stores result, and triggers a re-render.
  const fetchMediaSize = useCallback((url: string) => {
    if (sizesRef.current[url] !== undefined) return;
    sizesRef.current[url] = null;
    setSizeVersion((v) => v + 1);

    apiClient
      .get('/marketing/media-size', { params: { url } })
      .then((res) => {
        const size = res.data?.data?.size ?? res.data?.size ?? 0;
        sizesRef.current[url] = size;
        setSizeVersion((v) => v + 1);
      })
      .catch(() => {
        sizesRef.current[url] = 0;
        setSizeVersion((v) => v + 1);
      });
  }, []);

  // On initial load or when mediaUrls change (e.g. editing an existing
  // template that already has media), fetch sizes for any unknown URLs.
  useEffect(() => {
    mediaUrls.forEach((url) => {
      if (sizesRef.current[url] !== undefined) return;
      fetchMediaSize(url);
    });
  }, [mediaUrls, fetchMediaSize]);

  // Clean up removed URLs from the cache
  useEffect(() => {
    const urlSet = new Set(mediaUrls);
    for (const k of Object.keys(sizesRef.current)) {
      if (!urlSet.has(k)) delete sizesRef.current[k];
    }
  }, [mediaUrls]);

  // Compute totals from the ref (sizeVersion dependency ensures re-calc)
  let totalSize = 0;
  let loadingCount = 0;
  mediaUrls.forEach((url) => {
    const s = sizesRef.current[url];
    if (s === null || s === undefined) loadingCount++;
    else if (s > 0) totalSize += s;
  });
  const sizePct = Math.min((totalSize / MMS_MAX_BYTES) * 100, 100);
  const color = sizeColorClass(totalSize);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (varOpen && varPanelRef.current && !varPanelRef.current.contains(e.target as Node)) {
        setVarOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [varOpen]);

  const insertAtCursor = useCallback(
    (text: string) => {
      const ta = textareaRef.current;
      if (!ta) {
        onMessageChange(messageBody + text);
        return;
      }
      const s = ta.selectionStart;
      const e = ta.selectionEnd;
      const newVal = messageBody.substring(0, s) + text + messageBody.substring(e);
      onMessageChange(newVal);
      requestAnimationFrame(() => {
        ta.focus();
        ta.selectionStart = ta.selectionEnd = s + text.length;
      });
    },
    [messageBody, onMessageChange],
  );

  const removeMedia = useCallback(
    (index: number) => {
      const removed = mediaUrls[index];
      if (removed) delete sizesRef.current[removed];
      onMediaChange(mediaUrls.filter((_, i) => i !== index));
      setSizeVersion((v) => v + 1);
    },
    [mediaUrls, onMediaChange],
  );

  // Exactly replicates PHP SmsEditor.addMedia:
  //   if fileSize is known → store it directly
  //   else → call fetchMediaSize immediately
  const addMedia = useCallback(
    (url: string, fileSize?: number) => {
      if (mediaUrls.length >= MMS_MAX_FILES) {
        toast.error(`MMS limited to ${MMS_MAX_FILES} files`);
        return;
      }
      // Every attachment arrives here — the library picker, an upload, a template
      // loaded into the composer — so this is the one place that can promise no
      // unsendable image reaches mediaUrls. The picker greys these out before the
      // click; this is what makes it true rather than merely likely.
      if (!isMmsSendableUrl(url)) {
        toast.error(mmsMediaRejectionReason(url));
        return;
      }
      if (fileSize && fileSize > 0) {
        sizesRef.current[url] = fileSize;
        setSizeVersion((v) => v + 1);
      } else {
        fetchMediaSize(url);
      }
      onMediaChange([...mediaUrls, url]);
    },
    [mediaUrls, onMediaChange, toast, fetchMediaSize],
  );

  return (
    <>
      <div className="border border-slate-200 rounded-lg focus-within:border-sky-400 focus-within:ring-2 focus-within:ring-sky-100">
        {/* Header: char count + segment badge */}
        <div className="flex items-center justify-between px-3 py-1.5 bg-slate-50 border-b border-slate-100 rounded-t-lg">
          <span className="text-xs text-slate-500">
            <span>{charCount.toLocaleString()}</span>/{SMS_MAX_CHARS.toLocaleString()}
          </span>
          <div className="flex items-center gap-1.5">
            <span
              className={cn(
                'inline-block px-2 py-0.5 rounded text-xs font-semibold text-white',
                segmentColorClass(segments),
              )}
            >
              {segments}X
            </span>
            <div className="group relative">
              <Info className="h-3.5 w-3.5 text-slate-400 cursor-help" />
              <div className="hidden group-hover:block absolute top-full right-0 mt-1 w-64 bg-slate-800 text-white text-xs leading-relaxed p-2.5 rounded-md z-50 shadow-lg">
                SMS messages are billed per segment. A standard segment is 160 characters (70 if using emojis or special characters). Aim for 1-4 segments for best deliverability and cost efficiency. Messages over 10 segments may be split or rejected by carriers.
              </div>
            </div>
          </div>
        </div>

        {/* Textarea */}
        <textarea
          ref={textareaRef}
          className="w-full min-h-[200px] px-3 py-3 text-sm leading-relaxed resize-y border-none outline-none font-sans"
          placeholder="Type your SMS message..."
          value={messageBody}
          onChange={(e) => onMessageChange(e.target.value)}
        />

        {/* Opt-out instructions — the SMS twin of the email unsubscribe link.
            Every message goes out with them: saving adds {{opt_out}} when it is
            missing, sending is refused without it, and the send pipeline appends
            it as a last resort. Shown once there is a message to opt out of. */}
        {hasSmsMessageContent(messageBody) && (
          hasSmsOptOut(messageBody) ? (
            <div className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] text-slate-500 border-t border-slate-100">
              <Check className="h-3.5 w-3.5 shrink-0 text-green-500" />
              <span>
                Opt-out instructions included.
                {usesOptOutToken && optOutText && (
                  <> <code>{SMS_OPT_OUT_TOKEN}</code> sends &ldquo;{optOutText}&rdquo;.</>
                )}
              </span>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3 px-3 py-1.5 text-[11px] text-amber-800 bg-amber-50 border-t border-amber-100">
              <span className="flex items-center gap-1.5 min-w-0">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                <span>
                  No opt-out instructions. <code className="font-semibold">{SMS_OPT_OUT_TOKEN}</code>
                  {optOutText && <> (&ldquo;{optOutText}&rdquo;)</>} is added to the end of the message when you save.
                </span>
              </span>
              <button
                type="button"
                onClick={() => onMessageChange(ensureSmsOptOut(messageBody))}
                className="shrink-0 font-semibold text-amber-900 underline hover:no-underline"
              >
                Add now
              </button>
            </div>
          )
        )}

        {/* Media preview */}
        {mediaUrls.length > 0 && (
          <div className="flex flex-wrap gap-2 px-3 py-2">
            {mediaUrls.map((url, i) => (
              <div key={i} className="relative w-14 h-14 rounded overflow-hidden border border-slate-200">
                <img src={url} alt="Media" className="w-full h-full object-cover" />
                <button
                  onClick={() => removeMedia(i)}
                  className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-black/60 text-white text-[10px] flex items-center justify-center hover:bg-black/80"
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* MMS size bar (matches PHP sms-editor.php) */}
        {mediaUrls.length > 0 && (
          <div className="px-3 py-2">
            <div className="flex items-center justify-between mb-1">
              <span className="inline-flex items-center gap-1">
                <span className="text-[11px] font-semibold text-slate-600">MMS Media Size</span>
                <div className="group relative">
                  <Info className="h-3 w-3 text-slate-400 cursor-help" />
                  <div className="hidden group-hover:block absolute bottom-full left-0 mb-2 w-64 bg-slate-800 text-white text-[11px] leading-relaxed p-2.5 rounded-md z-50 shadow-lg pointer-events-none">
                    MMS messages are limited to 5 MB total media and 10 files max. For best quality, keep images under 600 KB each. Videos over 5 MB cannot be sent via MMS — include a link instead.
                  </div>
                </div>
              </span>
              <span className={cn('text-[11px] font-semibold', SIZE_TEXT[color])}>
                {formatBytes(totalSize)} / 5 MB
              </span>
            </div>

            {/* Progress track */}
            <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
              <div
                className={cn('h-full rounded-full transition-all duration-300', FILL_BG[color])}
                style={{ width: `${sizePct}%` }}
              />
            </div>

            {/* Over-limit warning */}
            {totalSize > MMS_MAX_BYTES && (
              <p className="text-[11px] text-red-500 font-medium mt-1">
                Total media exceeds the 5 MB MMS limit. Remove or compress files before sending.
              </p>
            )}

            {/* File count note */}
            <p className="text-[11px] text-slate-500 mt-1">
              {mediaUrls.length} of {MMS_MAX_FILES} files
              {loadingCount > 0 && ` (${loadingCount} loading size...)`}
            </p>
          </div>
        )}

        {/* Toolbar */}
        <div className="relative flex items-center gap-1 px-2.5 py-2 bg-slate-50 border-t border-slate-100 rounded-b-lg">
          {/* Photo picker button */}
          <button
            onClick={() => setPhotoPickerOpen(true)}
            className="w-8 h-8 flex items-center justify-center rounded hover:bg-slate-200 text-slate-500 hover:text-slate-700 transition-colors"
            title="Add image from photo library"
          >
            <Image className="h-4 w-4" />
          </button>

          {/* Emoji button + picker */}
          <div className="relative">
            <button
              onClick={() => { setEmojiOpen(!emojiOpen); setVarOpen(false); }}
              className="w-8 h-8 flex items-center justify-center rounded hover:bg-slate-200 text-slate-500 hover:text-slate-700 transition-colors"
              title="Insert emoji"
            >
              <Smile className="h-4 w-4" />
            </button>
            {emojiOpen && (
              <SmsEmojiPicker
                onSelect={(emoji) => {
                  insertAtCursor(emoji);
                  setEmojiOpen(false);
                }}
                onClose={() => setEmojiOpen(false)}
              />
            )}
          </div>

          {/* Variable button + panel */}
          <div className="relative" ref={varPanelRef}>
            <button
              onClick={() => { setVarOpen(!varOpen); setEmojiOpen(false); }}
              className="w-8 h-8 flex items-center justify-center rounded hover:bg-slate-200 text-slate-500 hover:text-slate-700 transition-colors"
              title="Insert variable"
            >
              <Code className="h-4 w-4" />
            </button>
            {varOpen && (
              <div className="absolute bottom-full left-0 mb-1 w-80 bg-white border border-slate-200 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
                <p className="px-3 py-2 text-[11px] text-slate-400 border-b border-slate-100">
                  Missing values use your account defaults. Override per-variable inline: <code>{'{{first_name [[Friend]]}}'}</code>
                </p>
                {SMS_VARIABLES.map((v) => {
                  const varName = v.key.replace(/^\{\{|\}\}$/g, '');
                  const defaultVal = varName === 'opt_out' ? optOutText : varDefaults[varName];
                  return (
                    <button
                      key={v.key}
                      onClick={() => { insertAtCursor(v.key); setVarOpen(false); }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b border-slate-50 last:border-b-0"
                    >
                      <span className="flex items-center justify-between">
                        <code className="font-semibold text-sky-500">{v.key}</code>
                        <span className="text-[11px] text-slate-400">{v.desc}</span>
                      </span>
                      {defaultVal && (
                        <span className="block text-[10px] text-slate-500 italic mt-0.5">Default: {defaultVal}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Photo picker modal */}
      <SmsPhotoPicker
        open={photoPickerOpen}
        onClose={() => setPhotoPickerOpen(false)}
        onSelect={(url, fileSize) => {
          addMedia(url, fileSize);
          setPhotoPickerOpen(false);
        }}
        wrapperClassName={photoPickerZClassName}
      />
    </>
  );
}
